import { apiClient, getErrorMessage } from './apiClient'
import * as indexedDB from './indexedDBService'
import { addToSyncQueue } from './syncService'
import type { Cliente } from '../types'

export interface PaginationResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface ClienteFilters {
  page?: number
  limit?: number
  search?: string
  status?: 'all' | 'ativo' | 'inativo'
  sort?: string
}

export interface ClienteCreateRequest {
  razaoSocial: string
  nomeFantasia: string
  cnpj: string
  email: string
  telefone: string
  cep: string
  logradouro: string
  numero: string
  complemento?: string
  bairro: string
  cidade: string
  estado: string
}

// Função auxiliar para obter clientes (exportada para sincronização)
export async function getClientes(): Promise<Cliente[]> {
  const response = await apiClient.get<PaginationResponse<Cliente>>('/clientes?limit=9999')
  return response.data.data
}

export const clienteService = {
  async getAll(filters?: ClienteFilters): Promise<PaginationResponse<Cliente>> {
    try {
      // Se online, busca do servidor
      if (navigator.onLine) {
        const params = new URLSearchParams()
        if (filters?.page) params.append('page', filters.page.toString())
        if (filters?.limit) params.append('limit', filters.limit.toString())
        if (filters?.search) params.append('search', filters.search)
        if (filters?.status && filters.status !== 'all') params.append('status', filters.status)
        if (filters?.sort) params.append('sort', filters.sort)

        const response = await apiClient.get<PaginationResponse<Cliente>>(`/clientes?${params}`)
        return response.data
      }

      // Se offline, busca do IndexedDB
      const clientes = await indexedDB.getAll<Cliente>(indexedDB.STORES.CLIENTES)
      
      // Aplica filtros localmente
      let filtered = clientes
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase()
        filtered = clientes.filter(c => 
          c.razaoSocial?.toLowerCase().includes(searchLower) ||
          c.nomeFantasia?.toLowerCase().includes(searchLower) ||
          c.cnpj?.includes(searchLower)
        )
      }

      return {
        data: filtered,
        pagination: {
          page: 1,
          limit: filtered.length,
          total: filtered.length,
          totalPages: 1,
        }
      }
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async getById(id: string): Promise<Cliente> {
    try {
      // Se online, busca do servidor
      if (navigator.onLine) {
        const response = await apiClient.get<Cliente>(`/clientes/${id}`)
        return response.data
      }

      // Se offline, busca do IndexedDB
      const cliente = await indexedDB.get<Cliente>(indexedDB.STORES.CLIENTES, id)
      if (!cliente) throw new Error('Cliente não encontrado')
      return cliente
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async create(data: ClienteCreateRequest): Promise<Cliente> {
    try {
      // Se online, envia ao servidor
      if (navigator.onLine) {
        const response = await apiClient.post<Cliente>('/clientes', data)
        // Salva no IndexedDB também
        await indexedDB.put(indexedDB.STORES.CLIENTES, response.data)
        return response.data
      }

      // Se offline, salva localmente e adiciona à fila
      const tempId = `temp_${Date.now()}`
      const cliente: Cliente = {
        id: tempId,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Cliente

      await indexedDB.put(indexedDB.STORES.CLIENTES, cliente)
      // Adiciona à fila com o ID temporário para referência
      await addToSyncQueue('CREATE', 'cliente', { ...data, id: tempId })
      
      return cliente
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async update(id: string, data: Partial<ClienteCreateRequest>): Promise<Cliente> {
    try {
      // Se online, envia ao servidor
      if (navigator.onLine) {
        const response = await apiClient.put<Cliente>(`/clientes/${id}`, data)
        // Atualiza no IndexedDB também
        await indexedDB.put(indexedDB.STORES.CLIENTES, response.data)
        return response.data
      }

      // Se offline, atualiza localmente e adiciona à fila
      const cliente = await indexedDB.get<Cliente>(indexedDB.STORES.CLIENTES, id)
      if (!cliente) throw new Error('Cliente não encontrado')

      const updated = {
        ...cliente,
        ...data,
        updatedAt: new Date().toISOString(),
      }

      await indexedDB.put(indexedDB.STORES.CLIENTES, updated)
      await addToSyncQueue('UPDATE', 'cliente', { id, ...data })
      
      return updated
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async delete(id: string): Promise<void> {
    try {
      // Se online, deleta no servidor
      if (navigator.onLine) {
        await apiClient.delete(`/clientes/${id}`)
        // Remove do IndexedDB também
        await indexedDB.remove(indexedDB.STORES.CLIENTES, id)
        return
      }

      // Se offline, remove localmente e adiciona à fila
      await indexedDB.remove(indexedDB.STORES.CLIENTES, id)
      await addToSyncQueue('DELETE', 'cliente', { id })
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },
}
